import React, { useState, useEffect } from 'react';
import { Button, Modal, Row, Col, DatePicker, Select, Form, Input, InputNumber } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Option } = Select;

const parseProjectConfig = (config) => {
  if (config == null) return {};
  if (typeof config === 'string') {
    const t = config.trim();
    if (!t) return {};
    try {
      const p = JSON.parse(t);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof config === 'object' && !Array.isArray(config)) return { ...config };
  return {};
};

const ProjectDetailsModal = ({
  selectedProject,
  onUpdateProject,
  projectTeamMembersAccessList,
  userAccessList = [],
}) => {
  const isAdmin = userAccessList.includes('admin') || userAccessList.includes('super-admin');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [startDateInput, setStartDateInput] = useState(
    selectedProject?.startDate ? moment(selectedProject.startDate) : null
  );
  const [endDateInput, setEndDateInput] = useState(
    selectedProject?.endDate ? moment(selectedProject.endDate) : null
  );
  const [selectedStatus, setSelectedStatus] = useState(selectedProject?.projectStatus || 1);
  const [dayShiftInputHour, setDayShiftInputHour] = useState(
    selectedProject?.config?.technicalLogDefaultShiftHours?.day || 0
  );
  const [nightShiftInputHour, setNightShiftInputHour] = useState(
    selectedProject?.config?.technicalLogDefaultShiftHours?.night || 0
  );
  const [ndtRejectMaxPercent, setNdtRejectMaxPercent] = useState(undefined);

  const [projectForm] = Form.useForm();

  const projectId = selectedProject?.id;

  useEffect(() => {
    const cfg = parseProjectConfig(selectedProject?.config);
    setDayShiftInputHour(cfg.technicalLogDefaultShiftHours?.day || 0);
    setNightShiftInputHour(cfg.technicalLogDefaultShiftHours?.night || 0);
    const pt = selectedProject?.projectType || '';
    const showNdtReject = pt === 'Welding' || pt === 'Both';
    const n = cfg.ndtRejectMaxPercent;
    setNdtRejectMaxPercent(
      showNdtReject && n !== undefined && n !== null && n !== ''
        ? Number(n)
        : undefined
    );
  }, [selectedProject]);

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSave = async () => {
    try {
      await projectForm.validateFields();
      if (projectId) {
        const prevCfg = parseProjectConfig(selectedProject?.config);
        const nextConfig = {
          ...prevCfg,
          technicalLogDefaultShiftHours: {
            day: dayShiftInputHour,
            night: nightShiftInputHour,
          },
        };
        const ptSave = selectedProject?.projectType || '';
        const weldingOrBothSave = ptSave === 'Welding' || ptSave === 'Both';
        if (
          weldingOrBothSave &&
          ndtRejectMaxPercent !== undefined &&
          ndtRejectMaxPercent !== null &&
          !Number.isNaN(Number(ndtRejectMaxPercent))
        ) {
          nextConfig.ndtRejectMaxPercent = Number(ndtRejectMaxPercent);
        } else {
          delete nextConfig.ndtRejectMaxPercent;
        }
        const updateData = {
          startDate: moment(startDateInput).format('MM-DD-YYYY'),
          endDate: moment(endDateInput).format('MM-DD-YYYY'),
          projectStatus: selectedStatus,
          config: nextConfig,
        };
        await onUpdateProject({ data: updateData, id: projectId });
      }
      setIsModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <>
      <Button
        type="primary"
        onClick={showModal}
        shape="round"
        icon={
          <InfoCircleOutlined
            style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: 8 }}
          />
        }
      >
        <span style={{ verticalAlign: 'middle' }}>Project Details</span>
      </Button>

      <Modal
        width="50vw"
        className="pipeline-modal"
        title={`Project :- ${selectedProject?.projectName || ''}`}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          isAdmin && (
            <Button key="save" type="primary" onClick={handleSave}>
              Save
            </Button>
          ),
        ]}
      >
        <Form
          form={projectForm}
          name="basic"
          layout="vertical"
          autoComplete="off"
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Client"
                name="clientName"
                initialValue={selectedProject?.clientName}
              >
                <Input
                  placeholder="Enter Client Name"
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Manager"
                name="manager"
                initialValue={selectedProject?.manager}
              >
                <Input
                  placeholder="Enter Manager Name"
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Start Date"
                name="startDate"
                initialValue={startDateInput}
              >
                <DatePicker
                  allowClear={false}
                  format="DD-MM-YYYY"
                  defaultValue={startDateInput}
                  onChange={(date) => setStartDateInput(date)}
                  disabled={!isAdmin}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="End Date"
                name="endDate"
                initialValue={endDateInput}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  allowClear={false}
                  defaultValue={endDateInput}
                  onChange={(date) => setEndDateInput(date)}
                  disabledDate={(current) =>
                    startDateInput && current < moment(startDateInput, 'DD-MM-YYYY')
                  }
                  disabled={!isAdmin}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Project Status"
                name="projectStatus"
                initialValue={selectedStatus}
              >
                <Select
                  className="client-select"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  disabled={!isAdmin}
                >
                  <Option value={1}>Initiated</Option>
                  <Option value={2}>Active</Option>
                  <Option value={3}>On Hold</Option>
                  <Option value={4}>Completed</Option>
                  <Option value={5}>Facility</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Completed Welds"
                name="completedWelds"
                initialValue="-"
              >
                <Input
                  placeholder="Completed Welds"
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Technician Log Default Day/Night Shift"
                name="technicianLog"
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>Day (hrs)</div>
                    <InputNumber
                      min={0}
                      value={dayShiftInputHour}
                      onChange={(v) => setDayShiftInputHour(Number(v ?? 0))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>Night (hrs)</div>
                    <InputNumber
                      min={0}
                      value={nightShiftInputHour}
                      onChange={(v) => setNightShiftInputHour(Number(v ?? 0))}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </Form.Item>
            </Col>
            {(selectedProject?.projectType === 'Welding' ||
              selectedProject?.projectType === 'Both') && (
              <Col span={8}>
                <Form.Item label="Max NDT reject % (client limit)">
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    placeholder="e.g. 2"
                    value={ndtRejectMaxPercent}
                    onChange={(v) => setNdtRejectMaxPercent(v)}
                    style={{ width: '100%' }}
                    size="large"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectDetailsModal;
